import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_GURU } from "@/lib/nav";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Plus, X } from "lucide-react";
import { FilterBar } from "@/components/FilterBar";
import { AutoGrowTextarea, DeskripsiButton } from "@/components/Deskripsi";
import { EP_ALL, EP_TAHUN, EP_SEMESTER, epSemesterLabel } from "@/lib/constants";

const F_EMPTY = { siswa: "", guru: "", tahun: "", semester: "", jenis: "" };
const NEW_EMPTY = { siswa_id: "", jenis: "", tahun: EP_TAHUN[0], semester: EP_SEMESTER[0].v, nilai: "", deskripsi: "" };

export default function EPNilaiGuru() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [myAssign, setMyAssign] = useState([]); // assignments where guru_id = me
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(NEW_EMPTY);

  const load = useCallback(async () => {
    try {
      const [{ data: n }, { data: p }] = await Promise.all([
        api.get("/ekskul-progsus/nilai"),
        api.get("/ekskul-progsus/pendampingan"),
      ]);
      setItems(n);
      // Only my own assignments
      setMyAssign(p.filter((a) => a.guru_id === user.linked_id));
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  }, [user]);
  useEffect(() => { load(); }, [load]);

  // Siswa dropdown options = unique siswa I'm pendamping for (any jenis)
  const mySiswa = useMemo(() => {
    const seen = {};
    myAssign.forEach((a) => { if (!seen[a.siswa_id]) seen[a.siswa_id] = { id: a.siswa_id, nama: a.nama_siswa, nis: a.nis_siswa }; });
    return Object.values(seen).sort((a, b) => a.nama.localeCompare(b.nama));
  }, [myAssign]);

  // Jenis options for selected siswa = only jenis where I'm pendamping
  const jenisForSelected = useMemo(() => {
    if (!form.siswa_id) return [];
    return myAssign.filter((a) => a.siswa_id === form.siswa_id).map((a) => a.jenis);
  }, [form.siswa_id, myAssign]);

  const visible = items.filter((it) => {
    if (applied.siswa && !(it.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.guru && !(it.nama_guru || "").toLowerCase().includes(applied.guru.toLowerCase())) return false;
    if (applied.tahun && String(it.tahun) !== String(applied.tahun)) return false;
    if (applied.semester && it.semester !== applied.semester) return false;
    if (applied.jenis && it.jenis !== applied.jenis) return false;
    return true;
  });

  const submit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/ekskul-progsus/nilai", {
        siswa_id: form.siswa_id,
        jenis: form.jenis,
        tahun: parseInt(form.tahun, 10),
        semester: form.semester,
        nilai: parseInt(form.nilai, 10),
        deskripsi: form.deskripsi,
      });
      toast.success("Nilai tersimpan");
      setShowForm(false); setForm(NEW_EMPTY);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_GURU}>
      <PageTitle
        title="Ekskul & Progsus — Nilai"
        action={
          <button onClick={() => { setShowForm(true); setForm(NEW_EMPTY); }} className="pp-btn-primary" data-testid="ep-nilai-guru-open">
            <Plus className="w-4 h-4" /> Input Nilai
          </button>
        }
      />

      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="ep-nilai-guru-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari nama siswa" },
          { key: "guru", type: "text", label: "Guru Pendamping", placeholder: "Cari pendamping" },
          { key: "jenis", type: "select", label: "Jenis Ekskul/Progsus", options: EP_ALL },
          { key: "tahun", type: "select", label: "Tahun", options: EP_TAHUN.map(String) },
          { key: "semester", type: "select", label: "Semester", options: EP_SEMESTER.map((s) => ({ v: s.v, label: s.label })) },
        ]}
      />

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="ep-nilai-guru-table">
            <thead><tr className="pp-table-header">
              <th className="text-left px-4 py-3">Nama Siswa</th>
              <th className="text-left px-4 py-3">Jenis Ekskul & Progsus</th>
              <th className="text-left px-4 py-3">Pendamping</th>
              <th className="text-left px-4 py-3">Tahun</th>
              <th className="text-left px-4 py-3">Semester</th>
              <th className="text-left px-4 py-3">Nilai</th>
              <th className="text-left px-4 py-3">Deskripsi</th>
            </tr></thead>
            <tbody>
              {visible.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-slate-400 italic">Belum ada nilai.</td></tr>}
              {visible.map((it) => (
                <tr key={it.id} className="pp-row">
                  <td className="px-4 py-3 font-medium">{it.nama_siswa}</td>
                  <td className="px-4 py-3 text-xs">{it.jenis}</td>
                  <td className="px-4 py-3 text-xs">{it.nama_guru}</td>
                  <td className="px-4 py-3">{it.tahun}</td>
                  <td className="px-4 py-3 text-xs">{epSemesterLabel(it.semester)}</td>
                  <td className="px-4 py-3 font-semibold">{it.nilai}</td>
                  <td className="px-4 py-3"><DeskripsiButton text={it.deskripsi} dataTestid={`ep-nilai-guru-desk-${it.id}`} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {showForm && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4" onClick={() => setShowForm(false)}>
          <div className="bg-white rounded-2xl max-w-lg w-full p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-xl font-bold">Input Nilai Ekskul & Progsus</h3>
              <button onClick={() => setShowForm(false)}><X className="w-5 h-5 text-slate-400" /></button>
            </div>
            {mySiswa.length === 0 && (
              <div className="text-sm text-slate-500 italic py-4">Anda belum menjadi pendamping ekskul/progsus siswa manapun. Hubungi Kepala Sekolah untuk penugasan.</div>
            )}
            {mySiswa.length > 0 && (
              <form onSubmit={submit} className="grid gap-4" data-testid="ep-nilai-guru-form">
                <div>
                  <label className="pp-label">Nama Siswa *</label>
                  <select required className="pp-input" value={form.siswa_id}
                    onChange={(e) => setForm({ ...form, siswa_id: e.target.value, jenis: "" })}
                    data-testid="ep-nilai-guru-siswa"
                  >
                    <option value="">— Pilih Siswa —</option>
                    {mySiswa.map((s) => <option key={s.id} value={s.id}>{s.nama}{s.nis ? ` — ${s.nis}` : ""}</option>)}
                  </select>
                </div>
                <div>
                  <label className="pp-label">Jenis Ekskul & Progsus *</label>
                  <select required className="pp-input" value={form.jenis}
                    onChange={(e) => setForm({ ...form, jenis: e.target.value })}
                    data-testid="ep-nilai-guru-jenis"
                    disabled={!form.siswa_id}
                  >
                    <option value="">— Pilih Jenis —</option>
                    {jenisForSelected.map((j) => <option key={j} value={j}>{j}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="pp-label">Tahun *</label>
                    <select required className="pp-input" value={form.tahun} onChange={(e) => setForm({ ...form, tahun: e.target.value })} data-testid="ep-nilai-guru-tahun">
                      {EP_TAHUN.map((y) => <option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="pp-label">Semester *</label>
                    <select required className="pp-input" value={form.semester} onChange={(e) => setForm({ ...form, semester: e.target.value })} data-testid="ep-nilai-guru-semester">
                      {EP_SEMESTER.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="pp-label">Nilai (0-100) *</label>
                  <input type="number" min={0} max={100} required className="pp-input"
                    value={form.nilai}
                    onChange={(e) => setForm({ ...form, nilai: e.target.value })}
                    data-testid="ep-nilai-guru-value"
                  />
                </div>
                <div>
                  <label className="pp-label">Deskripsi</label>
                  <AutoGrowTextarea
                    value={form.deskripsi}
                    onChange={(v) => setForm({ ...form, deskripsi: v })}
                    placeholder="Tulis deskripsi / catatan perkembangan siswa pada ekskul & progsus ini…"
                    dataTestid="ep-nilai-guru-deskripsi"
                  />
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button type="button" onClick={() => setShowForm(false)} className="pp-btn-secondary">Batal</button>
                  <button type="submit" className="pp-btn-primary" data-testid="ep-nilai-guru-submit">Simpan</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </Layout>
  );
}
